import { lazy, Suspense, useState, useCallback, type ReactNode } from 'react';
import useMFAEnforcement from '../hooks/useMFAEnforcement';
import { getFirebaseAuth } from '../services/firebase.ts';

const PasskeyEnrollment = lazy(() => import('./PasskeyEnrollment'));

export interface MFAEnrollmentGateProps {
  children: ReactNode;
}

/**
 * MFAEnrollmentGate
 *
 * Wraps protected content and blocks access for clinical roles (admin, doctor,
 * nurse, provider) until they have enrolled at least one passkey for MFA.
 * This is a HIPAA compliance requirement.
 *
 * If the user's role does not require MFA, children render immediately.
 */
const MFAEnrollmentGate = ({ children }: MFAEnrollmentGateProps) => {
  const { mfaRequired, mfaEnforced, isLoading } = useMFAEnforcement() as {
    mfaRequired: boolean;
    mfaEnforced: boolean;
    isLoading: boolean;
  };
  const [enrollmentSatisfied, setEnrollmentSatisfied] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const shouldEnforceMfa = mfaEnforced && !enrollmentSatisfied;

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      const { signOut } = await import('firebase/auth');
      const auth = await getFirebaseAuth();
      await signOut(auth);
    } catch (error: unknown) {
      console.error('Failed to sign out:', error);
      setSigningOut(false);
    }
  }, []);

  // While checking enrollment status, show a loading indicator
  if (mfaRequired && isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-slate-500 dark:text-slate-400">
          Checking security requirements...
        </p>
      </div>
    );
  }

  // If MFA is required but user has NOT enrolled, block access and show enrollment
  if (shouldEnforceMfa) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-4">
        <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900 p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="mx-auto w-14 h-14 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/40">
              <svg
                className="w-7 h-7 text-amber-600 dark:text-amber-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              Multi-factor authentication required
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Your role requires multi-factor authentication for HIPAA compliance.
              Please enroll a passkey to continue.
            </p>
          </div>

          {/* HIPAA notice */}
          <div className="rounded-xl bg-primary-50 dark:bg-primary-900/30 p-4">
            <p className="text-sm text-primary-800 dark:text-primary-300">
              As a healthcare provider with access to protected health information (PHI),
              you are required to set up a passkey (such as Touch ID, Face ID, or a
              hardware security key) before accessing the system.
            </p>
          </div>

          {/* Passkey Enrollment Component */}
          <Suspense
            fallback={
              <div className="rounded-lg p-4 bg-slate-50 dark:bg-slate-800">
                <div className="flex items-center space-x-2">
                  <div className="animate-spin h-4 w-4 border-2 border-primary-500 border-t-transparent rounded-full"></div>
                  <span className="text-slate-600 dark:text-slate-300">
                    Loading passkey setup...
                  </span>
                </div>
              </div>
            }
          >
            <PasskeyEnrollment onEnrollmentChange={setEnrollmentSatisfied} />
          </Suspense>

          {/* Sign Out option */}
          <div className="pt-2 border-t border-slate-200 dark:border-slate-700">
            <button
              onClick={handleSignOut}
              disabled={signingOut}
              className="w-full text-center text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors disabled:opacity-50"
            >
              {signingOut ? 'Signing out...' : 'Sign out instead'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // MFA not required or already enrolled — render children normally
  return children;
};

export default MFAEnrollmentGate;
